// ABOUTME: Header notifications bell — dropdown of persistent Notification
// rows. Extracted from Header.tsx to keep it under the 600-line cap.

import { useNavigate } from 'react-router-dom'
import {
  Bell,
  AlertTriangle,
  CheckCircle,
  Loader2,
  Megaphone,
  UserPlus,
  FileText,
  CreditCard,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useNotificationsStore } from '@/stores/useNotificationsStore'
import {
  followNotificationRoute,
  isNotificationClickable,
  resolveNotificationRoute,
} from '@/lib/notificationRoute'
import { groupBursts } from '@/lib/notificationBursts'
import type { NotificationType } from '@/mocks/data/types'

function getNotificationIcon(type: NotificationType) {
  // Map backend NotificationType enum values to UI icon categories
  if (['lead_received', 'lead_qualified'].includes(type)) {
    return <UserPlus className="text-success h-4 w-4 shrink-0" />
  }
  if (
    [
      'content_published',
      'content_failed',
      'concepts_ready',
      'content_plan_failed',
      'publish_failed',
    ].includes(type)
  ) {
    return <FileText className="text-chart-6 h-4 w-4 shrink-0" />
  }
  if (['budget_alert', 'low_credits'].includes(type)) {
    return <CreditCard className="text-warning h-4 w-4 shrink-0" />
  }
  if (['token_expired', 'token_expiring_soon'].includes(type)) {
    return <AlertTriangle className="text-warning h-4 w-4 shrink-0" />
  }
  if (['system_alert', 'agent_approval'].includes(type)) {
    return <Megaphone className="text-primary h-4 w-4 shrink-0" />
  }
  return <Bell className="text-muted-foreground h-4 w-4 shrink-0" />
}

/**
 * Compact unread-count pill anchored to the top-right of an icon button.
 * min-w + px (not a fixed width) keeps single digits circular while "9+"
 * widens into a pill without overflowing the 36px button bounds.
 */
export function NotificationBadge({ count, testId }: { count: number; testId: string }) {
  return (
    <span
      className="bg-destructive text-destructive-foreground pointer-events-none absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[0.625rem] leading-none font-medium tabular-nums"
      data-testid={testId}
    >
      {count > 9 ? '9+' : count}
    </span>
  )
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (seconds < 60) return 'Just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`
  return date.toLocaleDateString()
}

export function NotificationsBell() {
  const navigate = useNavigate()
  const {
    notifications,
    hasLoaded,
    markAsRead,
    markAllAsRead,
    getUnreadCount: getNotificationUnreadCount,
  } = useNotificationsStore()
  const shown = groupBursts(notifications).slice(0, 5)

  const notificationUnreadCount = getNotificationUnreadCount()

  return (
    // Opening the bell marks nothing read: an item is read when it is opened, or with
    // "Mark all read", so a glance never hides what still needs the owner.
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Notifications"
          className="relative size-11 md:size-9"
          data-testid="notification-bell"
        >
          <Bell className="size-5" />
          {notificationUnreadCount > 0 && (
            <NotificationBadge count={notificationUnreadCount} testId="notification-count" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80" data-testid="notification-dropdown">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifications</span>
          {notificationUnreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto px-2 py-1 text-xs"
              data-testid="mark-all-read-button"
              onClick={(e) => {
                e.preventDefault()
                markAllAsRead()
              }}
            >
              Mark all read
            </Button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {!hasLoaded && notifications.length === 0 ? (
          // First fetch still in flight — a definitive "all caught up" here
          // would be a confident lie that only self-corrects at the next poll.
          <div
            className="text-muted-foreground py-6 text-center text-sm"
            data-testid="notifications-loading"
          >
            <Loader2 className="mx-auto mb-2 h-8 w-8 animate-spin opacity-50" />
            <p>Loading notifications…</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-muted-foreground py-6 text-center text-sm">
            <CheckCircle className="mx-auto mb-2 h-8 w-8 opacity-50" />
            <p>No notifications</p>
            <p className="mt-1 text-xs">You're all caught up!</p>
          </div>
        ) : (
          <div className="max-h-72 overflow-y-auto">
            {shown.map(({ notification, ids, count }) => {
              const body = (
                <>
                  <div className="flex w-full items-start gap-2">
                    {getNotificationIcon(notification.type)}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <span className="line-clamp-2 text-sm font-medium break-words">
                          {notification.title}
                          {count > 1 && (
                            <span className="text-muted-foreground font-normal"> ×{count}</span>
                          )}
                        </span>
                        <span className="text-muted-foreground shrink-0 text-xs">
                          {formatTimeAgo(notification.created_at)}
                        </span>
                      </div>
                      <p className="text-muted-foreground mt-0.5 line-clamp-2 text-xs break-words">
                        {notification.body}
                      </p>
                    </div>
                  </div>
                  {!notification.read_at && (
                    <span className="bg-primary absolute top-2 right-2 h-2 w-2 rounded-full" />
                  )}
                </>
              )
              // A row the resolver has no destination for renders as plain
              // content, not a menu item: a DropdownMenuItem looks and behaves
              // clickable, so clicking one that leads nowhere is exactly the
              // silent no-op is about.
              if (!isNotificationClickable(notification)) {
                return (
                  <div
                    key={notification.id}
                    className="relative flex cursor-default flex-col items-start gap-1 px-3 py-2 text-sm"
                    data-testid={`notification-item-${notification.id}`}
                    data-clickable="false"
                  >
                    {body}
                  </div>
                )
              }
              return (
                <DropdownMenuItem
                  key={notification.id}
                  className="relative cursor-pointer flex-col items-start gap-1 px-3 py-2"
                  data-testid={`notification-item-${notification.id}`}
                  data-clickable="true"
                  onClick={() => {
                    for (const id of ids) markAsRead(id)
                    followNotificationRoute(resolveNotificationRoute(notification), navigate)
                  }}
                >
                  {body}
                </DropdownMenuItem>
              )
            })}
          </div>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="justify-center text-sm font-medium"
          data-testid="view-all-notifications"
          onClick={() => navigate('/notifications')}
        >
          View all notifications
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
